/**
 * admin/capabilities — framework-agnostic RBAC role → capability map.
 *
 * Mirrors the backend capability gating (campaign #18, Track B). The back-office
 * UI uses this to (a) decide whether a user may enter the admin area at all and
 * (b) hide/disable individual actions a role can't perform. The backend remains
 * the source of truth and re-checks every mutation; this is purely a UX layer so
 * the client never offers an action that would 403.
 *
 * Roles (broad → narrow):
 *   - super_admin — everything, including destructive GDPR removals + role grants
 *   - admin       — manage users/flags, but not destructive removals or granting
 *                   privileged roles
 *   - support     — read users + audit, ban/unban + revoke sessions (no role/flag
 *                   changes, no removals)
 *   - read_only   — read-only access to every admin panel
 *   - user        — no admin access
 */

import RBAC_CONTRACT from "./rbac-contract.json";

export const ADMIN_ROLES = ["super_admin", "admin", "support", "read_only", "user"] as const;

export type AdminRole = (typeof ADMIN_ROLES)[number];

/** Discrete capabilities the back-office UI gates on.*/
export type AdminCapability =
  | "admin.access"
  | "users.read"
  | "users.setRole"
  | "users.ban"
  | "users.revokeSessions"
  | "users.remove"
  | "audit.read"
  | "integrations.read"
  | "integrations.testEmail"
  | "flags.read"
  | "flags.write"
  | "metrics.read"
  /**
   * Mutating back-office content: themes, sources, templates and email
   * campaigns. Mirror of `Capability::ManageContent`. It was missing from this list, so
   * the UI had no way to hide those actions from `read_only` — and the backend
   * did not gate them either: they were actually executable.
   */
  | "content.write";

const READ_CAPS: AdminCapability[] = [
  "admin.access",
  "users.read",
  "audit.read",
  "integrations.read",
  "flags.read",
  "metrics.read",
];

const SUPPORT_CAPS: AdminCapability[] = [
  ...READ_CAPS,
  "users.ban",
  "users.revokeSessions",
  "integrations.testEmail",
  "content.write",
];

const ADMIN_CAPS: AdminCapability[] = [...SUPPORT_CAPS, "users.setRole", "flags.write"];

const SUPER_ADMIN_CAPS: AdminCapability[] = [...ADMIN_CAPS, "users.remove"];

const CAPABILITIES_BY_ROLE: Record<AdminRole, AdminCapability[]> = {
  super_admin: SUPER_ADMIN_CAPS,
  admin: ADMIN_CAPS,
  support: SUPPORT_CAPS,
  read_only: READ_CAPS,
  user: [],
};

function isAdminRole(role: string | null | undefined): role is AdminRole {
  return typeof role === "string" && (ADMIN_ROLES as readonly string[]).includes(role);
}

/** Narrows an arbitrary string (e.g. `session.user.role`) to a known role.*/
export function toAdminRole(role: string | null | undefined): AdminRole {
  return isAdminRole(role) ? role : "user";
}

/** Returns the capability set for a role string (unknown → `user` → none).*/
export function capabilitiesForRole(role: string | null | undefined): ReadonlySet<AdminCapability> {
  return new Set(CAPABILITIES_BY_ROLE[toAdminRole(role)]);
}

/** True when the role grants the given capability.*/
export function roleCan(role: string | null | undefined, capability: AdminCapability): boolean {
  return capabilitiesForRole(role).has(capability);
}

/** True when the role may enter the admin back-office at all.*/
export function isAdminCapable(role: string | null | undefined): boolean {
  return roleCan(role, "admin.access");
}

/**
 * Hierarchical role order, READ from the shared contract — not copied.
 *
 * The model has to exist in two runtimes (the server decides, the UI hides
 * what the server will reject), but the contract is a single one:
 * `rbac-contract.json`. Rust reads it in its tests and asserts that its `Role::rank()`
 * matches; here it is used directly at runtime, so this side cannot even
 * get out of sync.
 *
 * It is written key by key (and not with `Object.fromEntries` + `as Record<…>`)
 * so the compiler checks that the contract contains the five roles, instead of
 * an assertion inventing them without looking.
 */
const ROLE_RANK: Record<AdminRole, number> = {
  super_admin: RBAC_CONTRACT.roles.super_admin.rank,
  admin: RBAC_CONTRACT.roles.admin.rank,
  support: RBAC_CONTRACT.roles.support.rank,
  read_only: RBAC_CONTRACT.roles.read_only.rank,
  user: RBAC_CONTRACT.roles.user.rank,
};

/** Hierarchical rank of a role (0 = regular user). */
export function roleRank(role: string | null | undefined): number {
  return ROLE_RANK[toAdminRole(role)];
}

/**
 * Roles that `actorRole` can GRANT from the user manager.
 *
 * Replaces the old `ASSIGNABLE_ROLES = ADMIN_ROLES` constant (the ENTIRE
 * list, for anyone): a constant's name does not require saying
 * *who can assign it*, and this function's signature does. Mirror of
 * `can_grant_role` on the server: nobody grants above their own rank.
 */
export function assignableRolesFor(actorRole: string | null | undefined): readonly AdminRole[] {
  if (!roleCan(actorRole, "users.setRole")) return [];
  const rank = roleRank(actorRole);
  return ADMIN_ROLES.filter((r) => ROLE_RANK[r] <= rank);
}

/**
 * Can the actor administer (ban, revoke, change role, delete) a
 * user whose CURRENT role is `targetRole`? Mirror of `can_administer`:
 * strictly below, except for `super_admin` over peers.
 */
export function canAdministerUser(
  actorRole: string | null | undefined,
  targetRole: string | null | undefined,
): boolean {
  if (toAdminRole(actorRole) === "super_admin") return true;
  return roleRank(targetRole) < roleRank(actorRole);
}
